import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import { type Team } from '../types';
import { useTranslation } from 'react-i18next';

const PencilButton: React.FC<{ onClick: (e: React.MouseEvent) => void; title?: string }> = ({ onClick, title }) => (
    <button
        onClick={onClick}
        className="ml-2 p-2 rounded hover:bg-gray-700 text-gray-300 hover:text-white transition-colors"
        title={title || 'Edytuj'}
        aria-label={title || 'Edytuj'}
    >
        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M16.862 3.487a2.1 2.1 0 013.001 2.94l-9.8 9.8a4 4 0 01-1.632.97l-3.06.918.918-3.06a4 4 0 01.97-1.633l9.803-9.803z" />
            <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-2-2" />
        </svg>
    </button>
);

// helpers
const norm = (s?: string) => (s || '').toString().trim().toLowerCase();
const sportKey = (team: Team) => {
    const anyTeam = team as unknown as { discipline?: string; sportType?: string };
    return norm(anyTeam.discipline ?? anyTeam.sportType ?? '');
};
const truncate = (txt?: string, max = 250) => {
    const s = (txt || '').trim();
    if (s.length <= max) return s;
    const cut = s.slice(0, max);
    const space = cut.lastIndexOf(' ');
    return (space > 150 ? cut.slice(0, space) : cut).trim() + '…';
};

const TeamsPage: React.FC = () => {
    const [teams, setTeams] = useState<Team[]>([]);
    const { user } = useAuth();
    const { t } = useTranslation();
    const navigate = useNavigate();

    useEffect(() => {
        if (user) {
            api.get(`/Teams/user/${user.nameid}`)
                .then(res => setTeams(res.data))
                .catch(err => console.error('Błąd podczas pobierania drużyn:', err));
        }
    }, [user]);

    const sportLabel = (key: string) => {
        if (!key) return t('common.unknown') || 'Unknown';
        const i18nKey = `sportTypes.${key}` as const;
        const label = t(i18nKey);
        return label && label !== i18nKey ? label : key.charAt(0).toUpperCase() + key.slice(1);
    };

    // grupowanie po dyscyplinie + sortowanie alfabetyczne po nazwie
    const grouped = useMemo(() => {
        const map = new Map<string, Team[]>();
        for (const tm of teams) {
            const key = sportKey(tm);
            const bucket = map.get(key) || [];
            bucket.push(tm);
            map.set(key, bucket);
        }
        for (const [k, arr] of Array.from(map.entries())) {
            arr.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
            map.set(k, arr);
        }
        return map;
    }, [teams]);

    const orderedKeys = useMemo(() => {
        const keys = Array.from(grouped.keys());
        return keys.sort((a, b) => sportLabel(a).localeCompare(sportLabel(b)));
    }, [grouped]);

    return (
        <div className="container mx-auto p-4 md:p-6 bg-gray-900 text-white min-h-screen">
            <div className="flex justify-between items-center mb-6">
                <h1 className="text-3xl font-bold">{t('teamsPage.myTeams')}</h1>
                <Link to="/create-team" className="bg-green-600 hover:bg-green-700 text-white font-bold py-2 px-4 rounded-lg transition-colors">
                    {t('teamsPage.createTeam')}
                </Link>
            </div>

            {teams.length === 0 ? (
                <p className="text-center text-gray-400 mt-8">{t('teamsPage.noTeams')}</p>
            ) : (
                <div className="space-y-10">
                    {orderedKeys.map(key => {
                        const list = grouped.get(key) || [];
                        if (list.length === 0) return null;

                        return (
                            <section key={key}>
                                <div className="flex items-center justify-between mb-4">
                                    <h2 className="text-2xl font-bold text-white">
                                        {sportLabel(key)}
                                        <span className="ml-3 text-sm px-2 py-1 rounded-full bg-gray-800 text-gray-300 align-middle">{list.length}</span>
                                    </h2>
                                </div>

                                <div className="space-y-4">
                                    {list.map(team => (
                                        <div key={team.id} className="bg-gray-800 rounded-lg shadow-lg p-5">
                                            {/* header: nazwa + akcje */}
                                            <div className="flex items-start justify-between">
                                                <Link to={`/teams/${team.id}`} className="group flex-1 min-w-0">
                                                    <h3 className="text-xl font-bold text-white group-hover:text-blue-400 transition-colors truncate">
                                                        {team.name}
                                                    </h3>
                                                </Link>

                                                <div className="flex items-center">
                                                    <PencilButton
                                                        title={t('common.edit') || 'Edytuj'}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            navigate(`/teams/edit/${team.id}`);
                                                        }}
                                                    />
                                                </div>
                                            </div>

                                            {/* meta: logo + opis */}
                                            <div className="mt-3 flex items-start gap-4">
                                                {team.logoUrl ? (
                                                    <img
                                                        src={team.logoUrl}
                                                        alt={`${team.name} logo`}
                                                        className="w-16 h-16 object-cover rounded-md flex-shrink-0 bg-gray-700"
                                                        onError={(ev) => { (ev.currentTarget as HTMLImageElement).style.display = 'none'; }}
                                                    />
                                                ) : (
                                                    <div className="w-16 h-16 rounded-md bg-gray-700 flex items-center justify-center text-gray-400 flex-shrink-0">
                                                        LOGO
                                                    </div>
                                                )}

                                                <div className="flex-1 min-w-0">
                                                    <p className="text-gray-300 whitespace-pre-line break-words">
                                                        {truncate((team as any).clubInfo)}
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </section>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

export default TeamsPage;
